import { SettingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Dropdown, Table } from "antd";
import type { TableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState, type Key } from "react";

type Column<T> = ColumnsType<T>[number];
const pageSizeOptions = [100, 200, 300, 400, 500].map(String);

type PersistedTableProps<T> = TableProps<T> & {
  tableId?: string;
  enableColumnSettings?: boolean;
};

export function PersistedTable<T extends object>({
  columns = [],
  tableId,
  enableColumnSettings = true,
  scroll,
  ...props
}: PersistedTableProps<T>) {
  const derivedTableId = useMemo(() => tableId ?? buildTableId(columns), [columns, tableId]);
  const configurableColumns = useMemo(
    () => columns
      .map((column, index) => ({ column, key: getColumnKey(column, index), label: getColumnLabel(column, index) }))
      .filter((item) => !isMandatoryActionColumn(item.column) && item.label),
    [columns]
  );
  const [visibleKeys, setVisibleKeys] = usePersistedColumnKeys(derivedTableId, configurableColumns.map((item) => item.key));
  const visibleColumns = useMemo(
    () => enableColumnSettings ? columns.filter((column, index) => isMandatoryActionColumn(column) || visibleKeys.includes(getColumnKey(column, index))) : columns,
    [columns, enableColumnSettings, visibleKeys]
  );
  const scrollX = useMemo(() => Math.max(sumColumnWidths(visibleColumns), Number((scroll as { x?: number })?.x) || 0), [scroll, visibleColumns]);
  const mergedPagination = props.pagination === false
    ? false
    : { pageSize: 100, showSizeChanger: true, pageSizeOptions, ...(props.pagination ?? {}) };

  return (
    <div className="data-grid-shell">
      {enableColumnSettings && configurableColumns.length ? (
        <div className="data-grid-toolbar">
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "columns",
                  label: (
                    <Checkbox.Group
                      className="column-chooser"
                      value={visibleKeys}
                      options={configurableColumns.map((item) => ({ label: item.label, value: item.key }))}
                      onChange={(keys) => setVisibleKeys(keys.map(String))}
                    />
                  )
                }
              ]
            }}
          >
            <Button size="small" icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
        </div>
      ) : null}
      <Table<T> {...props} pagination={mergedPagination} columns={visibleColumns} scroll={{ ...scroll, x: scrollX || undefined }} />
    </div>
  );
}

function isMandatoryActionColumn<T>(column: Column<T>) {
  return String((column as { key?: Key }).key ?? "").toLocaleLowerCase() === "actions";
}

function usePersistedColumnKeys(tableId: string, defaultKeys: string[]) {
  const storageKey = `accounting-spa:columns:${tableId}`;
  const [visibleKeys, setVisibleKeysState] = useState<string[]>(() => readStoredColumnKeys(storageKey, defaultKeys));

  useEffect(() => {
    setVisibleKeysState(readStoredColumnKeys(storageKey, defaultKeys));
  }, [storageKey, defaultKeys.join("|")]);

  const setVisibleKeys = (keys: string[]) => {
    const next = keys.filter((key) => defaultKeys.includes(key));
    const safeKeys = next.length ? next : defaultKeys;
    setVisibleKeysState(safeKeys);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(safeKeys));
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }
  };

  return [visibleKeys, setVisibleKeys] as const;
}

function readStoredColumnKeys(storageKey: string, defaultKeys: string[]) {
  if (typeof window === "undefined") return defaultKeys;
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : null;
    if (!Array.isArray(parsed)) return defaultKeys;
    const keys = parsed.map(String).filter((key) => defaultKeys.includes(key));
    return keys.length ? keys : defaultKeys;
  } catch {
    return defaultKeys;
  }
}

function getColumnKey<T>(column: Column<T>, index: number) {
  const columnWithKey = column as { key?: Key; dataIndex?: Key | Key[] };
  if (columnWithKey.key !== undefined) return String(columnWithKey.key);
  if (Array.isArray(columnWithKey.dataIndex)) return columnWithKey.dataIndex.join(".");
  if (columnWithKey.dataIndex !== undefined) return String(columnWithKey.dataIndex);
  return `column-${index}`;
}

function getColumnLabel<T>(column: Column<T>, index: number) {
  const title = (column as { title?: unknown }).title;
  if (typeof title === "string") return title;
  if (typeof title === "number") return String(title);
  return `Column ${index + 1}`;
}

function buildTableId<T>(columns: ColumnsType<T>) {
  return columns.map((column, index) => getColumnKey(column, index)).join(":");
}

function sumColumnWidths<T>(columns: ColumnsType<T>) {
  return columns.reduce((total, column) => {
    const width = Number((column as { width?: number | string }).width);
    return total + (Number.isFinite(width) ? width : 180);
  }, 0);
}
